
import axios from "axios";

const API = process.env.REACT_APP_API_URL;

axios.defaults.withCredentials = true;

// ─── Types ────────────────────────────────────────────────────────────────────
export interface GTSVoice {
  name: string;
  displayName?: string;
  ssmlGender: "MALE" | "FEMALE" | "NEUTRAL" | "SSML_VOICE_GENDER_UNSPECIFIED";
  style?: string;
  provider?: string;
  naturalSampleRateHertz: number;
  languageCodes: string[];
}

export interface LanguageOption {
  code: string;
  label: string;
}

/** Always listed in language selectors (merged with locales from voices API). */
export const CORE_LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: "ru-RU", label: "Russian (ru-RU)" },
  { code: "uk-UA", label: "Ukrainian (uk-UA)" },
  { code: "et-EE", label: "Estonian (et-EE)" },
  { code: "lt-LT", label: "Lithuanian (lt-LT)" },
  { code: "es-ES", label: "Spanish (es-ES)" },
];

/** Used when voices API fails — merged after core locales so labels stay consistent. */
export const STANDARD_FALLBACK_LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: "lv-LV", label: "Latvian (lv-LV)" },
  { code: "en-US", label: "American English (en-US)" },
];

/** Dedupe by code; earlier lists win labels on duplicates. Sorted by label. */
export function mergeLanguageOptions(...lists: LanguageOption[][]): LanguageOption[] {
  const map = new Map<string, string>();
  for (const list of lists) {
    for (const { code, label } of list) {
      if (!map.has(code)) map.set(code, label);
    }
  }
  return Array.from(map.entries())
    .map(([code, label]) => ({ code, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Stub voice `languageCodes` when API is offline — covers core + fallback locales. */
export const OFFLINE_VOICE_LANGUAGE_CODES: string[] = Array.from(
  new Set([
    ...CORE_LANGUAGE_OPTIONS.map(l => l.code),
    ...STANDARD_FALLBACK_LANGUAGE_OPTIONS.map(l => l.code),
  ]),
);

export interface AdvancedOptions {
  speakingRate:     number;
  pitch:            number;
  volumeGainDb:     number;
  temperature:      number;
  topP:             number;
  topK:             number;
  stability:        number;
  similarityBoost:  number;
  style:            number;
  useSpeakerBoost:  boolean;
  audioEncoding:    "MP3" | "LINEAR16" | "OGG_OPUS";
  sampleRateHertz:  number;
  effectsProfileId: string;
}

/** Saved on project; new books inherit these sampling values unless overridden in the book form. */
export interface ProjectSamplingDefaults {
  temperature: number;
  topP:        number;
  topK:        number;
}

export interface IProject {
  _id:               string;
  projectName:       string;
  description:       string;
  editor:            string;
  partnerId?:        string | null;
  publisherId?:      string | null;
  defaultSampling?:  ProjectSamplingDefaults;
  bookCount?:        number;
  createdAt:         string;
}

export interface IBookEbook {
  fileName:    string;
  storagePath: string;
  mimeType:    string;
  size:        number;
  uploadedAt:  string;
}

export interface IBook {
  _id:               string;
  projectId:         string;
  title:             string;
  author?:           string;
  language:          string;
  model:             string;
  platform:          string;
  realVoice:         string;
  voiceLabel?:       string;
  styleInstructions?: string;
  notes?:             string;
  ebook?:            IBookEbook | null;
  chapterOrder?:     { name: string; order: number }[];
  advanced:          AdvancedOptions;
  createdAt:         string;
}

export interface IClip {
  _id:       string;
  bookId:    string;
  bookTitle: string;
  chapter:   string;
  clipName:  string;
  order:     number;
  version?:  number;
  scriptText?: string;
  charCount: number;
  audioUrl:  string;
  createdAt: string;
}

export interface IMergedChapter {
  _id:           string;
  bookId:        string;
  bookTitle:     string;
  chapterName:   string;
  audioUrl:      string;
  clipIds:       string[];
  clipCount:     number;
  totalChars:    number;
  totalDuration: number;
  createdAt:     string;
}

// ─── Service ──────────────────────────────────────────────────────────────────
const ttsService = {

  // ── Voices ──────────────────────────────────────────────────────────────
  getVoices: async (languageCode?: string, platform?: string): Promise<GTSVoice[]> => {
    const params: Record<string, string> = {};
    if (languageCode) params.language = languageCode;
    if (platform) params.platform = platform;
    const { data } = await axios.get(`${API}/tts/voices`, { params });
    return data;
  },

  /**
   * Voice list for UI selectors — not filtered by book language so any narrator
   * can be chosen for any locale (synthesis still uses book.language separately).
   */
  filterVoicesForSelection: (
    voices: GTSVoice[],
    opts?: { model?: string; platform?: string },
  ): GTSVoice[] => {
    if (!voices.length) return [];
    let list = voices;
    const model = (opts?.model || "").trim();
    const isEleven = String(opts?.platform || "").toLowerCase() === "elevenlabs";
    if (model && !isEleven) {
      const byModel = list.filter(v => v.name.includes(model));
      if (byModel.length) list = byModel;
    }
    return list;
  },

  buildLanguageList: (voices: GTSVoice[]): LanguageOption[] => {
    const map = new Map<string, string>();
    CORE_LANGUAGE_OPTIONS.forEach(l => map.set(l.code, l.label));
    STANDARD_FALLBACK_LANGUAGE_OPTIONS.forEach(l => map.set(l.code, l.label));
    voices.forEach(v =>
      v.languageCodes.forEach(lc => {
        if (!map.has(lc)) {
          try {
            const name = new Intl.DisplayNames(["en"], { type: "language" }).of(lc);
            map.set(lc, `${name} (${lc})`);
          } catch { map.set(lc, lc); }
        }
      })
    );
    return Array.from(map.entries())
      .map(([code, label]) => ({ code, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  },

  // ── Synthesize ───────────────────────────────────────────────────────────
  synthesize: async (payload: {
    text: string; voiceName: string; languageCode: string;
    platform?: string;
    styleInstructions: string; advanced: AdvancedOptions;
  }): Promise<{ audioContent: string; audioEncoding?: "MP3" | "LINEAR16" | "OGG_OPUS"; clipId: string }> => {
    const { data } = await axios.post(
      `${API}/tts/synthesize`,
      {
        text: payload.text,
        voiceName: payload.voiceName,
        languageCode: payload.languageCode,
        platform: payload.platform,
        styleInstructions: payload.styleInstructions,
        advanced: payload.advanced,
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      },
    );
    return data;
  },

  getElevenSubscription: async (): Promise<{
    character_count: number;
    character_limit: number;
    can_extend_character_limit: boolean;
  }> => {
    const { data } = await axios.get(`${API}/tts/elevenlabs/subscription`);
    return data;
  },

  base64ToBlobUrl: (base64: string, encoding: string): string => {
    const mime = ({ MP3: "audio/mpeg", LINEAR16: "audio/wav", OGG_OPUS: "audio/ogg" } as any)[encoding] ?? "audio/mpeg";
    const bytes = atob(base64);
    const arr   = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    return URL.createObjectURL(new Blob([arr], { type: mime }));
  },

  // ── Projects ─────────────────────────────────────────────────────────────
  getProjects: async (): Promise<IProject[]> => {
    const { data } = await axios.get(`${API}/tts/projects`);
    return data;
  },

  getProject: async (projectId: string): Promise<IProject> => {
    const { data } = await axios.get(`${API}/tts/projects/${projectId}`);
    return data;
  },

  createProject: async (payload: {
    projectName: string;
    description?: string;
    editor?: string;
    partnerId?: string | null;
    defaultSampling?: ProjectSamplingDefaults;
  }): Promise<{ projectId: string; projectName: string; partnerId?: string | null; defaultSampling?: ProjectSamplingDefaults }> => {
    const { data } = await axios.post(`${API}/tts/projects`, payload);
    return data;
  },

  assignProjectPartner: async (
    projectId: string,
    partnerId: string | null,
  ): Promise<{ projectId: string; projectName: string; partnerId: string | null }> => {
    const { data } = await axios.patch(`${API}/tts/projects/${projectId}/partner`, { partnerId });
    return data;
  },

  deleteProject: async (projectId: string): Promise<void> => {
    await axios.delete(`${API}/tts/projects/${projectId}`);
  },

  // ── Books ────────────────────────────────────────────────────────────────
  getBooksForProject: async (projectId: string): Promise<IBook[]> => {
    const { data } = await axios.get(`${API}/tts/projects/${projectId}/books`);
    return data;
  },

  createBook: async (projectId: string, payload: Partial<IBook>): Promise<{ bookId: string; title: string }> => {
    const { data } = await axios.post(`${API}/tts/projects/${projectId}/books`, payload);
    return data;
  },

  updateBook: async (bookId: string, payload: Partial<IBook>): Promise<IBook> => {
    const { data } = await axios.put(`${API}/tts/books/${bookId}`, payload);
    return data;
  },

  deleteBook: async (bookId: string): Promise<void> => {
    await axios.delete(`${API}/tts/books/${bookId}`);
  },

  renameChapter: async (bookId: string, payload: {
    oldName: string;
    newName?: string;
    order?: number;
  }): Promise<{
    updated: boolean;
    chapterName: string;
    order: number | null;
    chapters: { name: string; order: number }[];
    clipsUpdated: number;
    mergedUpdated: number;
  }> => {
    const { data } = await axios.patch(`${API}/tts/books/${bookId}/chapters/rename`, payload);
    return data;
  },

  getBookChapters: async (bookId: string): Promise<{ name: string; order: number }[]> => {
    const { data } = await axios.get(`${API}/tts/books/${bookId}/chapters`);
    return data.chapters;
  },

  addBookChapter: async (bookId: string, name: string): Promise<{
    chapterName: string;
    chapters: { name: string; order: number }[];
  }> => {
    const { data } = await axios.post(`${API}/tts/books/${bookId}/chapters`, { name });
    return data;
  },

  // ── Ebook attachment ────────────────────────────────────────────
  uploadBookEbook: async (bookId: string, payload: {
    fileName: string; dataBase64: string;
  }): Promise<IBook> => {
    const { data } = await axios.post(`${API}/tts/books/${bookId}/ebook`, payload);
    return data;
  },

  getBookEbookUrl: async (bookId: string): Promise<{
    url: string; fileName: string; mimeType: string; size: number;
  }> => {
    const { data } = await axios.get(`${API}/tts/books/${bookId}/ebook`);
    return data;
  },

  deleteBookEbook: async (bookId: string): Promise<IBook> => {
    const { data } = await axios.delete(`${API}/tts/books/${bookId}/ebook`);
    return data;
  },

  // ── Clips ────────────────────────────────────────────────────────────────
  getClipsForBook: async (bookId: string): Promise<IClip[]> => {
    const { data } = await axios.get(`${API}/tts/books/${bookId}/clips`);
    return data;
  },

  saveClip: async (bookId: string, payload: {
    chapter: string; clipName: string; audioBase64: string;
    audioEncoding: string; voiceName: string; languageCode: string;
    model: string; charCount: number; scriptText?: string;
  }): Promise<{ clipId: string; clipName: string; chapter: string; order: number; version?: number }> => {
    const { data } = await axios.post(`${API}/tts/books/${bookId}/clips`, payload);
    return data;
  },

  getClipAudio: async (clipId: string): Promise<{
    clipId: string; clipName: string; audioBase64: string; audioEncoding: string;
  }> => {
    const { data } = await axios.get(`${API}/tts/clips/${clipId}/audio`);
    return data;
  },

  renameClip: async (clipId: string, clipName: string): Promise<{ clipId: string; clipName: string }> => {
    const { data } = await axios.patch(`${API}/tts/clips/${clipId}`, { clipName });
    return data;
  },

  deleteClip: async (clipId: string): Promise<void> => {
    await axios.delete(`${API}/tts/clips/${clipId}`);
  },

  // ── Merge ────────────────────────────────────────────────────────────────
  mergeClips: async (bookId: string, payload: {
    clipIds: string[]; chapterName: string;
  }): Promise<{ mergedId: string; chapterName: string; clipCount: number; totalChars: number }> => {
    const { data } = await axios.post(`${API}/tts/books/${bookId}/merge`, payload);
    return data;
  },

  getMergedForBook: async (bookId: string): Promise<IMergedChapter[]> => {
    const { data } = await axios.get(`${API}/tts/books/${bookId}/merged`);
    return data;
  },

  getMergedAudio: async (mergedId: string): Promise<{
    mergedId: string; chapterName: string; audioBase64: string; audioEncoding: string;
  }> => {
    const { data } = await axios.get(`${API}/tts/merged/${mergedId}/audio`);
    return data;
  },

  deleteMerged: async (mergedId: string): Promise<void> => {
    await axios.delete(`${API}/tts/merged/${mergedId}`);
  },

  updateProject:async(id:string,projectName:string,description:string)=>{
    const response = await axios.post(`${API}/tts/project/${id}`,{projectName,description});
    return response
  }

};

export default ttsService;